import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Send, SplitSquareHorizontal, Bot, Gauge, Radio, ChevronDown, Clock, CheckCircle, XCircle, Loader2, Smartphone, Monitor, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { MessageToolbar } from "../components/MessageToolbar";

const API = '/api';

const COUNTRIES = [
  { code: "+549", name: "Argentina", flag: "AR", hint: "Sin 0 ni 15" },
  { code: "+52", name: "Mexico", flag: "MX", hint: "10 digitos" },
  { code: "+56", name: "Chile", flag: "CL", hint: "9 digitos" },
  { code: "+57", name: "Colombia", flag: "CO", hint: "10 digitos" },
  { code: "+51", name: "Peru", flag: "PE", hint: "9 digitos" },
  { code: "+598", name: "Uruguay", flag: "UY", hint: "8 digitos" },
  { code: "+595", name: "Paraguay", flag: "PY", hint: "9 digitos" },
  { code: "+591", name: "Bolivia", flag: "BO", hint: "8 digitos" },
  { code: "+593", name: "Ecuador", flag: "EC", hint: "9 digitos" },
  { code: "+58", name: "Venezuela", flag: "VE", hint: "10 digitos" },
  { code: "+34", name: "Espana", flag: "ES", hint: "9 digitos" },
  { code: "+1", name: "EEUU/Canada", flag: "US", hint: "10 digitos" },
  { code: "+55", name: "Brasil", flag: "BR", hint: "11 digitos" },
];

export default function ManualSend() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const manualTextareaRef = useRef(null);
  const partTextareaRefs = useRef([]);
  const splitToolbarRef = useRef(null);
  const [activeSplitPart, setActiveSplitPart] = useState(0);
  const [countryCode, setCountryCode] = useState("+549");
  const [localPhone, setLocalPhone] = useState("");
  const [form, setForm] = useState({
    profile_id: "",
    message: "",
    split_messages: false,
    human_typing: false,
    typing_speed: "normal",
    rcs_only: false
  });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const connectedProfiles = profiles.filter(p => p.status === "connected");
  const selectedProfile = connectedProfiles.find(p => p.id === form.profile_id);
  const isWhatsApp = selectedProfile?.type === "android" || selectedProfile?.type === "whatsapp_web";
  const selectedCountry = COUNTRIES.find(c => c.code === countryCode) || COUNTRIES[0];

  // --- Split message helpers ---
  const splitContent = (content) => {
    if (!content) return [""];
    const parts = content.split("---").map(p => p.trim());
    return parts.length > 0 ? parts : [""];
  };

  const updateMessagePart = (index, value) => {
    const parts = splitContent(form.message);
    parts[index] = value;
    setForm(prev => ({ ...prev, message: parts.join("\n---\n") }));
  };

  const addMessagePart = () => {
    const parts = splitContent(form.message);
    parts.push("");
    setForm(prev => ({ ...prev, message: parts.join("\n---\n") }));
    setActiveSplitPart(parts.length - 1);
  };

  const removeMessagePart = (index) => {
    const parts = splitContent(form.message).filter((_, i) => i !== index);
    if (parts.length === 0) parts.push("");
    setForm(prev => ({ ...prev, message: parts.join("\n---\n") }));
    if (activeSplitPart >= parts.length) setActiveSplitPart(Math.max(0, parts.length - 1));
  };

  const fetchData = useCallback(async () => {
    try {
      const [p] = await Promise.all([
        axios.get(`${API}/profiles`),
      ]);
      setProfiles(p.data);
      const connected = p.data.filter(pr => pr.status === "connected");
      if (connected.length > 0 && !form.profile_id) {
        setForm(prev => ({ ...prev, profile_id: connected[0].id }));
      }
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
     
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/manual-send/history`);
      setHistory(res.data);
    } catch {
      // silently handle
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchHistory();
    // Poll history every 15s
    const interval = setInterval(fetchHistory, 15000);
    return () => clearInterval(interval);
  }, [fetchData, fetchHistory]);

  const sendMessage = async () => {
    if (!form.profile_id || !localPhone || !form.message) {
      return toast.error("Completa perfil, telefono y mensaje");
    }
    const fullPhone = countryCode + localPhone.replace(/^0+/, "").replace(/^15/, "");
    setSending(true);
    try {
      const res = await axios.post(`${API}/manual-send`, {
        ...form,
        phone: fullPhone,
        attachment_ids: [],
        attachment_order: "after",
      });
      toast.success(isWhatsApp ? "Mensaje encolado para WhatsApp" : "Mensaje encolado para envio RCS");
      setHistory(prev => [res.data.message_log, ...prev]);
      setLocalPhone("");
      setForm(prev => ({ ...prev, message: "" }));
    } catch (e) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  const statusIcon = (status) => {
    switch (status) {
      case "sent": return <CheckCircle className="w-3 h-3 text-green-400" />;
      case "failed": return <XCircle className="w-3 h-3 text-red-400" />;
      case "pending": return <Loader2 className="w-3 h-3 text-yellow-400 animate-spin" />;
      default: return <Clock className="w-3 h-3 text-zinc-500" />;
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case "sent": return "Enviado";
      case "failed": return "Fallido";
      case "pending": return "En cola";
      case "skipped_not_rcs": return "Omitido (SMS)";
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="manual-send-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white uppercase">Envio Manual</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Envia un mensaje individual {isWhatsApp ? 'via WhatsApp' : 'via RCS'} desde la plataforma
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-zinc-900 border border-white/5 rounded-lg p-4 sm:p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
                  Perfil de Envio
                  {connectedProfiles.length === 0 && (
                    <span className="text-red-400 ml-2">(Sin perfiles conectados)</span>
                  )}
                </label>
                <div className="relative">
                  <select
                    data-testid="manual-profile-select"
                    value={form.profile_id}
                    onChange={(e) => setForm({ ...form, profile_id: e.target.value, rcs_only: false })}
                    className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors duration-200"
                  >
                    <option value="">Seleccionar perfil...</option>
                    {connectedProfiles.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.type === "android" ? "[WA]" : p.type === "whatsapp_web" ? "[WA Web]" : "[RCS]"} {p.name} ({p.phone_number || "sin numero"})
                      </option>
                    ))}
                  </select>
                  {selectedProfile && (
                    <span className={`absolute right-8 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded text-[9px] font-bold ${
                      isWhatsApp ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {isWhatsApp ? <><Smartphone className="w-3 h-3 inline mr-0.5" />WhatsApp</> : <><Monitor className="w-3 h-3 inline mr-0.5" />RCS</>}
                    </span>
                  )}
                </div>
                {connectedProfiles.length === 0 && profiles.length > 0 && (
                  <p className="text-[10px] text-amber-400 mt-1">
                    Conecta un perfil desde la extension o APK
                  </p>
                )}
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Telefono Destino</label>
                <div className="flex gap-2">
                  <select
                    data-testid="country-code-select"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-2 py-2 text-sm text-white outline-none transition-colors duration-200 w-28 flex-shrink-0"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.code}
                      </option>
                    ))}
                  </select>
                  <input
                    data-testid="manual-phone-input"
                    value={localPhone}
                    onChange={(e) => setLocalPhone(e.target.value.replace(/[^\d]/g, ""))}
                    className="flex-1 bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200"
                    placeholder="1123456789"
                  />
                </div>
                <p className="text-[10px] text-zinc-600 mt-1 font-mono">
                  {selectedCountry.name}: <span className="text-cyan-500">{selectedCountry.code}</span> + {selectedCountry.hint}
                </p>
              </div>
            </div>

            {/* Split toggle */}
            <div className="flex items-center justify-between bg-zinc-950/50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <SplitSquareHorizontal className="w-4 h-4 text-cyan-400" />
                <div>
                  <span className="text-xs text-zinc-300">Enviar en mensajes separados</span>
                  <p className="text-[10px] text-zinc-600">Cada bloque = un mensaje individual</p>
                </div>
              </div>
              <button
                data-testid="manual-toggle-split"
                onClick={() => setForm({ ...form, split_messages: !form.split_messages })}
                className={`w-10 h-5 rounded-full transition-colors duration-200 relative ${
                  form.split_messages ? 'bg-blue-600' : 'bg-zinc-700'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all duration-200 ${
                  form.split_messages ? 'left-5' : 'left-0.5'
                }`} />
              </button>
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
                {form.split_messages ? 'Mensajes Separados' : 'Mensaje'}
              </label>

              {form.split_messages ? (
                <div className="space-y-3">
                  <MessageToolbar
                    textareaRef={splitToolbarRef}
                    value={splitContent(form.message)[activeSplitPart] || ""}
                    onChange={(val) => updateMessagePart(activeSplitPart, val)}
                    hideDivider
                  />
                  {splitContent(form.message).map((part, i) => (
                    <div key={`manual-part-${i}`} className="relative group">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[9px] font-mono font-bold text-cyan-500 bg-cyan-500/10 px-2 py-0.5 rounded">
                          MSG {i + 1}
                        </span>
                        {splitContent(form.message).length > 1 && (
                          <button
                            data-testid={`manual-remove-part-${i}`}
                            onClick={() => removeMessagePart(i)}
                            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all duration-200"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <textarea
                        ref={el => { partTextareaRefs.current[i] = el; }}
                        data-testid={`manual-split-textarea-${i}`}
                        value={part}
                        onFocus={() => {
                          setActiveSplitPart(i);
                          splitToolbarRef.current = partTextareaRefs.current[i];
                        }}
                        onChange={(e) => updateMessagePart(i, e.target.value)}
                        rows={3}
                        className={`w-full bg-zinc-950/50 border rounded-md px-3 py-3 text-sm font-mono text-cyan-400/90 placeholder:text-zinc-600 outline-none transition-colors duration-200 leading-relaxed ${
                          activeSplitPart === i ? 'border-cyan-500/50 ring-1 ring-cyan-500/20' : 'border-zinc-800'
                        }`}
                        placeholder={`Escribe el mensaje ${i + 1}...`}
                      />
                    </div>
                  ))}
                  <button
                    data-testid="manual-add-msg-part-btn"
                    onClick={addMessagePart}
                    className="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors duration-200 bg-cyan-500/5 hover:bg-cyan-500/10 border border-dashed border-cyan-500/20 rounded-md px-3 py-2 w-full justify-center"
                  >
                    <Plus className="w-3.5 h-3.5" /> Agregar Mensaje
                  </button>
                </div>
              ) : (
                <>
                  <MessageToolbar
                    textareaRef={manualTextareaRef}
                    value={form.message}
                    onChange={(val) => setForm({ ...form, message: val })}
                  />
                  <textarea
                    ref={manualTextareaRef}
                    data-testid="manual-message-input"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={5}
                    className="w-full bg-zinc-950/50 border border-zinc-800 border-t-0 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-b-md px-3 py-3 text-sm font-mono text-cyan-400/90 placeholder:text-zinc-600 outline-none transition-colors duration-200 leading-relaxed"
                    placeholder={"Hola, este es un mensaje de prueba"}
                  />
                </>
              )}
              <p className="text-[10px] text-zinc-600 mt-1 font-mono">
                Click derecho en variable para limite de chars. Ej: {"{{nombreL20}}"} = max 20
              </p>
            </div>

            {/* Advanced Options */}
            <div className="space-y-3 bg-zinc-950/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bot className="w-3.5 h-3.5 text-purple-400" />
                  <span className="text-xs text-zinc-300">Escritura humana</span>
                  {isWhatsApp && <span className="text-[9px] text-green-400 font-mono">(recomendado WA)</span>}
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={form.human_typing}
                    onChange={e => setForm({...form, human_typing: e.target.checked})} />
                  <div className="w-9 h-5 bg-zinc-700 peer-checked:bg-purple-600 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>

              {form.human_typing && (
                <div className="flex items-center justify-between pl-5">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-3.5 h-3.5 text-zinc-400" />
                    <span className="text-xs text-zinc-400">Velocidad</span>
                  </div>
                  <select
                    data-testid="typing-speed-select"
                    value={form.typing_speed}
                    onChange={e => setForm({...form, typing_speed: e.target.value})}
                    className="bg-zinc-800 border border-zinc-700 rounded-md px-2 py-1 text-xs text-white outline-none"
                  >
                    <option value="slow">Lenta (50-150ms)</option>
                    <option value="normal">Normal (20-70ms)</option>
                    <option value="fast">Rapida (5-25ms)</option>
                  </select>
                </div>
              )}

              {!isWhatsApp && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Radio className="w-3.5 h-3.5 text-green-400" />
                    <span className="text-xs text-zinc-300">Solo enviar a RCS</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" checked={form.rcs_only}
                      onChange={e => setForm({...form, rcs_only: e.target.checked})}
                      data-testid="rcs-only-toggle" />
                    <div className="w-9 h-5 bg-zinc-700 peer-checked:bg-green-600 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                  </label>
                </div>
              )}
            </div>

            <button
              data-testid="send-manual-btn"
              onClick={sendMessage}
              disabled={sending}
              className={`w-full ${isWhatsApp ? 'bg-green-600 hover:bg-green-700 shadow-[0_0_15px_rgba(34,197,94,0.3)]' : 'bg-blue-600 hover:bg-blue-700 shadow-[0_0_15px_rgba(37,99,235,0.3)]'} text-white rounded-md px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors duration-200 active:scale-95 disabled:opacity-50`}
            >
              {isWhatsApp ? <Smartphone className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {sending ? "Enviando..." : isWhatsApp ? "Enviar via WhatsApp" : "Enviar via RCS"}
            </button>
          </div>
        </div>

        {/* Sidebar - Historial */}
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-3">
              Historial de Envios
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {historyLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-xs text-zinc-600 py-2">Sin envios recientes</p>
              ) : (
                history.map((h, i) => (
                  <div key={h.id || i} className={`bg-zinc-950/50 rounded-md p-2.5 border-l-2 ${
                    h.status === 'sent' ? 'border-green-500/50' :
                    h.status === 'failed' ? 'border-red-500/50' :
                    'border-yellow-500/50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-zinc-300 font-mono">{h.phone}</p>
                      <div className="flex items-center gap-1">
                        {statusIcon(h.status)}
                        <span className={`text-[10px] font-mono ${
                          h.status === 'sent' ? 'text-green-400' :
                          h.status === 'failed' ? 'text-red-400' :
                          'text-yellow-400'
                        }`}>{statusLabel(h.status)}</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-zinc-500 truncate mt-0.5">{h.message_text}</p>
                    <p className="text-[9px] text-zinc-600 font-mono mt-0.5">
                      {h.created_at ? new Date(h.created_at).toLocaleString('es-AR', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                      }) : ''}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
